import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { rolesGenerateMagicRolePrompt } from '@/api/roles';
import type { GenerateMagicRolePromptCommand } from '@/api/roles';
import { AIRewriteStep } from '@/app/components/PersonasManager/PersonaDetailDialog/AIRewrite/useAIRewrite';

interface RoleAIRewriteState {
  isOpen: boolean;
  currentStep: AIRewriteStep;
  originalDescription: string;
  customInstructions: string;
  rewrittenDescription: string | null;
  isLoading: boolean;
  error: string | null;
  name?: string;
  language?: string;
}

type MutationError = AxiosError<{ title?: string; message?: string }> | Error | null;

const getErrorMessage = (error: MutationError): string => {
  if (!error) {
    return 'Failed to generate rewrite. Please try again.';
  }

  if ('response' in (error as AxiosError) && (error as AxiosError).response) {
    const data = (error as AxiosError).response?.data as { title?: string; message?: string } | undefined;
    return data?.message || data?.title || (error as AxiosError).message || 'Failed to generate rewrite.';
  }

  return (error as Error).message || 'Failed to generate rewrite.';
};

export const useRoleAIRewrite = (onConfirm: (rewrittenText: string) => void) => {
  const [state, setState] = useState<RoleAIRewriteState>({
    isOpen: false,
    currentStep: AIRewriteStep.INSTRUCTIONS,
    originalDescription: '',
    customInstructions: '',
    rewrittenDescription: null,
    isLoading: false,
    error: null
  });

  const { mutate, isPending } = useMutation<string, MutationError, GenerateMagicRolePromptCommand>({
    mutationFn: async (payload) => {
      const response = await rolesGenerateMagicRolePrompt(payload);
      const data = (response as unknown as { data?: string })?.data;

      if (typeof data === 'string') {
        return data;
      }

      return (response as unknown as string) ?? '';
    },
    onSuccess: (rewrittenText) => {
      setState((prev) => ({
        ...prev,
        rewrittenDescription: rewrittenText,
        currentStep: AIRewriteStep.RESULT,
        isLoading: false,
        error: null
      }));
    },
    onError: (error) => {
      setState((prev) => ({
        ...prev,
        error: getErrorMessage(error),
        isLoading: false,
        currentStep: AIRewriteStep.INSTRUCTIONS
      }));
    }
  });

  const openDialog = useCallback((description: string, name?: string, language?: string) => {
    setState({
      isOpen: true,
      currentStep: AIRewriteStep.INSTRUCTIONS,
      originalDescription: description ?? '',
      customInstructions: '',
      rewrittenDescription: null,
      isLoading: false,
      error: null,
      name,
      language
    });
  }, []);

  const closeDialog = useCallback(() => {
    setState({
      isOpen: false,
      currentStep: AIRewriteStep.INSTRUCTIONS,
      originalDescription: '',
      customInstructions: '',
      rewrittenDescription: null,
      isLoading: false,
      error: null
    });
  }, []);

  const setCustomInstructions = useCallback((instructions: string) => {
    setState((prev) => ({ ...prev, customInstructions: instructions }));
  }, []);

  const submitRewrite = useCallback(() => {
    if (isPending) return;

    setState((prev) => ({
      ...prev,
      isLoading: true,
      currentStep: AIRewriteStep.LOADING,
      error: null
    }));

    mutate({
      text: state.originalDescription,
      instructions: state.customInstructions.trim() ? state.customInstructions : undefined,
      name: state.name,
      language: state.language
    });
  }, [isPending, mutate, state.customInstructions, state.language, state.name, state.originalDescription]);

  const confirmRewrite = useCallback(() => {
    if (!state.rewrittenDescription) return;
    onConfirm(state.rewrittenDescription);
    closeDialog();
  }, [closeDialog, onConfirm, state.rewrittenDescription]);

  const goBackToInstructions = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: AIRewriteStep.INSTRUCTIONS,
      rewrittenDescription: null,
      error: null
    }));
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const isLoading = useMemo(() => state.isLoading || isPending, [state.isLoading, isPending]);

  return {
    ...state,
    isLoading,
    openDialog,
    closeDialog,
    setCustomInstructions,
    submitRewrite,
    confirmRewrite,
    goBackToInstructions,
    clearError
  };
};
